// ============================================================================
// Géométrie déductive du noyau (lot 5.4) : les relations entre LONGUEURS.
// ============================================================================
// « AM = 6 », « IJ = BC ÷ 2 », « AM ÷ AB = AN ÷ AC = MN ÷ BC », « BC² ≠ AB² + AC² ».
// Une longueur s'écrit avec deux points de la figure (« AB » = « BA », clé triée) ;
// toutes les longueurs sont POSITIVES, ce qui rend équivalentes « AM ÷ AB = AN ÷ AC »
// et « AM × AC = AN × AB », ou « BC² = 25 » et « BC = 5 ».
// Une chaîne « a = b = c » se lit comme les relations consécutives (a = b, b = c).
// Les calculs sont exacts (forme normale du noyau : fractions, racines).
// ============================================================================

import * as Q from '../exact';
import {
  normalize,
  polyDivExact,
  polyIsZero,
  polySymbols,
  polyTerms,
  rfSimplify,
  rfDiv,
  rfToTerm,
  evalFloat,
  type RatFn,
  type Poly,
} from '../normal';
import {
  parseStatement,
  substitute,
  sym,
  sub,
  symbolsOf,
  termText,
  canon,
  type Term,
} from '../term';
import { univariateSolutions } from '../witness';

export type MOp = '=' | '≠';
export interface MRel {
  op: MOp;
  l: Term;
  r: Term;
}
/** Une ligne de longueurs : ses membres et les relations consécutives. */
export interface MLine {
  members: Term[];
  ops: MOp[];
  rels: MRel[];
}

export type MetricParse = { ok: true; m: MLine } | { ok: false; why: string };

/** « AB » → « AB », « BA » → « AB » ; null si ce n'est pas une longueur de la figure */
export function segmentName(n: string, points: ReadonlySet<string>): string | null {
  const m = /^([A-Z])([A-Z])$/.exec(n);
  if (!m || m[1] === m[2] || !points.has(m[1]!) || !points.has(m[2]!)) return null;
  return m[1]! < m[2]! ? m[1]! + m[2]! : m[2]! + m[1]!;
}

/**
 * Lit une ligne de longueurs (« AM ÷ AB = AN ÷ AC »). Les noms sont ramenés à leur
 * clé triée ; tout nom qui n'est pas une longueur de la figure est refusé.
 */
export function parseMetric(text: string, points: ReadonlySet<string>): MetricParse {
  const parts = text.split(/\s*(=|≠)\s*/);
  if (parts.length < 3)
    return {
      ok: false,
      why: 'Écris une égalité (« = ») ou une différence (« ≠ ») entre longueurs.',
    };
  const members: Term[] = [];
  const ops: MOp[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const raw = parts[i]!.trim();
    if (!raw) return { ok: false, why: 'Il manque un membre de part et d’autre du signe.' };
    const p = parseStatement(raw);
    if (!p.ok || p.s.k !== 'expr')
      return { ok: false, why: 'Une seule relation à la fois, avec « = » ou « ≠ ».' };
    const map: Record<string, Term> = {};
    for (const n of symbolsOf(p.s.e)) {
      const seg = segmentName(n, points);
      if (!seg) return { ok: false, why: `« ${n} » n’est pas une longueur de la figure.` };
      if (seg !== n) map[n] = sym(seg);
    }
    members.push(Object.keys(map).length ? substitute(p.s.e, map) : p.s.e);
    if (i + 1 < parts.length) ops.push(parts[i + 1] as MOp);
  }
  const rels = ops.map((op, i) => ({ op, l: members[i]!, r: members[i + 1]! }));
  return { ok: true, m: { members, ops, rels } };
}

export function mlineText(m: MLine): string {
  return m.members.map((t, i) => (i ? ` ${m.ops[i - 1]} ` : '') + termText(t)).join('');
}
export function relText(r: MRel): string {
  return `${termText(r.l)} ${r.op} ${termText(r.r)}`;
}

/** les longueurs d'une relation */
export function relSegments(r: MRel): Set<string> {
  return new Set([...symbolsOf(r.l), ...symbolsOf(r.r)]);
}

/* ------------------------------ forme normale ------------------------------ */

function diffOf(r: MRel): RatFn | null {
  const n = normalize(sub(r.l, r.r));
  return n.ok ? n.rf : null;
}
const isConstPoly = (p: Poly): boolean => polySymbols(p).size === 0;
/** un polynôme de signe constant sur les longueurs (toutes positives) */
function signedPoly(p: Poly): boolean {
  let s = 0;
  for (const { m, c } of polyTerms(p)) {
    if (m.some((f) => f.a.kind === 'opaque')) return false;
    const sg = Q.sign(c);
    if (sg === 0) continue;
    if (s && sg !== s) return false;
    s = sg;
  }
  return s !== 0;
}

/** Valeur de vérité d'une relation SANS longueur (null : il reste des longueurs). */
export function constTruth(r: MRel): boolean | null {
  const d = diffOf(r);
  if (!d || !isConstPoly(d.n) || !isConstPoly(d.d)) return null;
  const zero = polyIsZero(d.n);
  return r.op === '=' ? zero : !zero;
}

/**
 * Deux relations entre longueurs disent-elles la même chose ? (même signe, et
 * différences proportionnelles par un facteur de signe constant : « AM ÷ AB = AN ÷ AC »
 * et « AB ÷ AM = AC ÷ AN », « BC² = 25 » et « BC = 5 »)
 */
export function sameRelation(a: MRel, b: MRel): boolean {
  if (a.op !== b.op) return false;
  const da = diffOf(a);
  const db = diffOf(b);
  if (!da || !db) return false;
  const za = polyIsZero(da.n);
  const zb = polyIsZero(db.n);
  if (za || zb) return za === zb;
  // numérateurs proportionnels par un facteur de signe constant (« AM × AC − AN × AB »
  // et « AM ÷ AB − AN ÷ AC » : même numérateur, dénominateurs positifs)
  if (signedPoly(da.d) && signedPoly(db.d)) {
    const q1 = polyDivExact(da.n, db.n);
    if (q1 && signedPoly(q1)) return true;
    const q2 = polyDivExact(db.n, da.n);
    if (q2 && signedPoly(q2)) return true;
  }
  let q: RatFn;
  try {
    q = rfSimplify(rfDiv(da, db));
  } catch {
    return false;
  }
  return signedPoly(q.n) && signedPoly(q.d);
}

/* ------------------------------ valeurs connues ------------------------------ */

export type Values = ReadonlyMap<string, Term>;

export function substRel(r: MRel, V: Values): MRel {
  if (!V.size) return r;
  const map = Object.fromEntries(V);
  return { op: r.op, l: substitute(r.l, map), r: substitute(r.r, map) };
}

/**
 * Les longueurs connues : une égalité (donnée ou écrite) où il ne reste qu'UNE
 * longueur inconnue la détermine, si elle a une seule solution positive
 * (« BC² = 49 » donne BC = 7, « IJ = 8 ÷ 2 » donne IJ = 4).
 */
export function knownValues(rels: MRel[]): Map<string, Term> {
  const V = new Map<string, Term>();
  for (let round = 0; round < 12; round++) {
    let changed = false;
    for (const r0 of rels) {
      if (r0.op !== '=') continue;
      const r = substRel(r0, V);
      const segs = relSegments(r);
      if (segs.size !== 1) continue;
      const s = [...segs][0]!;
      if (V.has(s)) continue;
      const d = diffOf(r);
      if (!d) continue;
      const sol = univariateSolutions(d, s);
      if (!sol) continue;
      const pos = sol.roots.filter((t) => evalFloat(t, {}) > 1e-12);
      if (pos.length !== 1) continue;
      V.set(s, canon(rfToTerm(rfSimplify(normalizeOrThrow(pos[0]!)))));
      changed = true;
    }
    if (!changed) break;
  }
  return V;
}
function normalizeOrThrow(t: Term): RatFn {
  const n = normalize(t);
  if (!n.ok) throw new Error('valeur illisible');
  return n.rf;
}

/** Valeur exacte d'une expression de longueurs connues (null s'il reste une inconnue). */
export function valueOf(t: Term, V: Values): Term | null {
  const s = substitute(t, Object.fromEntries(V));
  if (symbolsOf(s).size) return null;
  const n = normalize(s);
  if (!n.ok) return null;
  return canon(rfToTerm(rfSimplify(n.rf)));
}

/* ------------------------------ calculs restants ------------------------------ */

const isInt = (t: Term): t is { k: 'num'; v: Q.Rat } => t.k === 'num' && Q.isInteger(t.v);
function irreducibleSqrt(t: Term): boolean {
  if (t.k !== 'fn' || t.f !== 'sqrt' || !isInt(t.a)) return false;
  const n = t.a.v.n;
  if (n < 2n) return false;
  const sf = Q.squarefreePart(n);
  return !!sf && sf.square === 1n;
}
function isFinalNumber(t: Term): boolean {
  if (t.k === 'num') return true;
  if (irreducibleSqrt(t)) return true;
  // fraction irréductible « 2 ÷ 3 »
  if (t.k === 'div' && isInt(t.a) && isInt(t.b)) {
    const b = t.b.v.n;
    const a = t.a.v.n;
    const g = gcd(a < 0n ? -a : a, b < 0n ? -b : b);
    return b > 1n && g === 1n;
  }
  // « 5√2 »
  if (t.k === 'mul' && t.c.length === 2 && t.c[0]!.k === 'num' && irreducibleSqrt(t.c[1]!))
    return true;
  return false;
}
const gcd = (a: bigint, b: bigint): bigint => (b === 0n ? a : gcd(b, a % b));

/**
 * Calculs encore à faire dans une écriture : opérations entre nombres (« 8 ÷ 2 »,
 * « 3² + 4² »), racines ou fractions simplifiables. Une longueur n'en compte pas.
 */
export function calcDebt(t: Term): number {
  if (t.k === 'num' || t.k === 'sym') return 0;
  if (isFinalNumber(t)) return 0;
  const kids: Term[] = t.k === 'add' || t.k === 'mul' ? t.c : t.k === 'fn' ? [t.a] : [t.a, t.b];
  const own = symbolsOf(t).size === 0 ? 1 : 0;
  return own + kids.reduce((s, k) => s + calcDebt(k), 0);
}
export function relDebt(r: MRel): number {
  return calcDebt(r.l) + calcDebt(r.r);
}

/* ------------------------------ figure ------------------------------ */

/** Vraie sur la figure ? (longueurs mesurées, tolérance relative) */
export function relHoldsIn(r: MRel, lengths: Readonly<Record<string, number>>): boolean {
  const a = evalFloat(r.l, lengths);
  const b = evalFloat(r.r, lengths);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const eq = Math.abs(a - b) <= 1e-7 * Math.max(1, Math.abs(a), Math.abs(b));
  return r.op === '=' ? eq : !eq;
}

/** écriture exacte d'une valeur (« 7 », « 2 ÷ 3 », « √41 ») */
export function valueText(t: Term): string {
  return termText(t);
}
